import { useContext, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

import AppConfig from 'bxcommon/providers/AppConfig';
import { RequestStepWrapper } from 'bxcommon/components/RequestStepWrapper/RequestStepWrapper';
import { FormWrapper } from 'bxcommon/components/FormWrapper/FormWrapper';
import { FORM_VARIANT } from 'bxcommon/constants/form.constant';
import { STEP_VARIANT } from 'bxcommon/constants/steps.constant';
import { useValidation, mapDegrees, DEGREES_MAPPING_OPTIONS } from 'bxcommon/helpers/form.helper';
import { STATUS } from 'bxcommon/constants/status.constant';
import { RequestContext } from 'bxcommon/context/Request.context';
import { AMENDMENT_TYPE } from 'bxcommon/constants/status.constant';

import { registryInitCheck } from '../../common/common.helpers';
import { AdvisorHeader } from '../AdvisorHeader/AdvisorHeader';
import { Intro } from '../Intro/Intro';
import { Step } from './Step';
import { DEFAULT_COMPANY } from './constants';
import { Confirmations } from './Confirmations';
import {
  activityValidationSchema,
  employerChangeValidationSchema,
  employerChangeCompanyValidationSchema,
  personalValidationSchema,
  professionalValidationSchema,
  codeOfConductValidationSchema,
  companyValidationSchema,
  formValidationSchema,
  detailsInitialValidationSchema,
  detailsRenewalValidationSchema,
  detailsEmployerChangeValidationSchema,
} from './RequestFormValidation';

import './RequestForm.scss';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const RequestForm = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { caseId } = useParams();

  const user = useSelector((state) => state.user.details);

  const { request, updateRequestField, list, formRef } = useContext(RequestContext);

  const canReinitialise = useRef(true);
  const isAmendment = request.amendment && AppConfig.editStatuses.includes(request.amendment.status);
  const [intro, showIntro] = useState(isAmendment);
  // When you want test Renewal process on local environment isRenewal change to true
  const isRenewal = request.amendment && request.amendment.type === AMENDMENT_TYPE.RENEWAL;
  const isEmployerChange = request.amendment && request.amendment.type === AMENDMENT_TYPE.EMPLOYER_CHANGE;
  const storedFormValues =
    (isRenewal || isEmployerChange) && sessionStorage.getItem('regsevices-form-values')
      ? JSON.parse(sessionStorage.getItem('regsevices-form-values'))
      : false;
  const getSessionStorage = storedFormValues && storedFormValues.case_id === request.case_id ? storedFormValues : false;
  const [isSummaryCompleted, setIsSummaryCompleted] = useState(false);
  const isUpdateRequire = request.status === STATUS.UPDATE_REQUIRED;

  useLayoutEffect(() => {
    if (
      request.case_id &&
      !AppConfig.editStatuses.includes(request.status) &&
      !(request.amendment && AppConfig.editStatuses.includes(request.amendment.status))
    ) {
      navigate('/request-details');
    }
  }, [navigate, request.case_id, request.status, request.amendment]);

  // Needed only for hydration data on entry straight to the /caseID url/  when in admin mode
  useEffect(() => {
    // TODO: debug why after first POST we have page re-render
    // Because of a hydration delay if entered from direct URL we need to enable hydrating for a moment
    // and then in next tick disable reinitialise as it messes up with file upload flow (first POST).
    const hydrateData = async () => {
      registryInitCheck(caseId, user, updateRequestField, list, request, navigate);
      await sleep(1000);
      canReinitialise.current = true;
    };
    hydrateData();
  }, [list, caseId, user, request, updateRequestField, navigate]);

  useEffect(() => {
    const handleSetCompanies = async () => {
      try {
        const companies = request.companies && (request.companies.length > 0 ? request.companies : [DEFAULT_COMPANY]);

        if (formRef.current) {
          await formRef.current.setFieldValue('companies', companies, false);
        }
      } catch (e) {
        console.error('Error setting companies:', e);
      }
    };

    if (!intro && !getSessionStorage) {
      handleSetCompanies();
    }
  }, [request.companies, formRef, intro, getSessionStorage]);

  const onFormSubmit = (values) => {
    values.education.degrees = mapDegrees(values.education.degrees_temp, DEGREES_MAPPING_OPTIONS.MAP);
  };

  const isCaseIdAndEmailSpecifiedInRequest = request.case_id && request.email;
  const adminModeNewRequest = user.is_admin && !isCaseIdAndEmailSpecifiedInRequest;
  const requestValues = getSessionStorage || request;
  const getSessionStorageEmail = JSON.parse(sessionStorage.getItem('regsevices-form-values-additional-email')) || '';

  const [activeStep, setActiveStep] = useState(1);
  // renewal must re-collect FinSA proof: wipe only backend-sourced values; values re-entered this renewal (sessionStorage) survive refresh
  const finsaResetActive = isRenewal && !isSummaryCompleted && activeStep <= 4 && !getSessionStorage;

  const initialValues = {
    first_name: user.is_admin ? requestValues.first_name || '' : requestValues.first_name || user.first_name,
    last_name: user.is_admin ? requestValues.last_name || '' : requestValues.last_name || user.last_name,
    email: adminModeNewRequest ? '' : requestValues.email || user.email,

    // personal
    address_line_1: requestValues.address_line_1 || '',
    address_line_2: requestValues.address_line_2 || '',
    postcode: requestValues.postcode || '',
    city: requestValues.city || '',
    country: requestValues.country || '',
    alternative_email: requestValues.alternative_email || '',
    phone_number: requestValues.phone_number || '',
    phone_number_dial_code: requestValues.phone_number_dial_code || '',
    work_phone_number: requestValues.work_phone_number || '',
    work_phone_number_dial_code: requestValues.work_phone_number_dial_code || '',
    date_of_birth: requestValues.date_of_birth ? dayjs(requestValues.date_of_birth) : null,
    correspondence_language: requestValues.correspondence_language || null,
    country_of_citizenship: requestValues.country_of_citizenship || '',
    additional_country_of_citizenship: requestValues.additional_country_of_citizenship || '',
    place_of_origin: requestValues.place_of_origin || '',

    // fields of activity
    fields_of_activity: requestValues.fields_of_activity || [],

    // education
    education: {
      // professional or academic degree
      degrees_temp:
        requestValues.education.degrees_temp ||
        mapDegrees(requestValues.education.degrees || [], DEGREES_MAPPING_OPTIONS.DETAILED_MAP),

      // professional trainings && knowledge according to FinSA and other
      has_predefined_certificates: Boolean(requestValues.education.has_predefined_certificates),
      predefined_certificates: requestValues.education.predefined_certificates || [],
      has_additional_certificates: Boolean(requestValues.education.has_additional_certificates),
      additional_certificates: requestValues.education.additional_certificates || '',
      has_employer_confirmation: Boolean(requestValues.education.has_employer_confirmation),
      has_finsa_certificates: Boolean(requestValues.education.has_finsa_certificates),
      finsa_certificates: finsaResetActive ? [] : requestValues.education.finsa_certificates || [],
    },

    employer_change_type: requestValues.employer_change_type || null,

    // company / job / ombudsman office
    companies:
      requestValues.companies && requestValues.companies.length > 0 ? requestValues.companies : [DEFAULT_COMPANY],

    // documents
    passport_files: requestValues.passport_files || [],
    cv_files: requestValues.cv_files || [],
    criminal_record_extract_files: requestValues.criminal_record_extract_files || [],
    professional_or_academic_degree_files: requestValues.professional_or_academic_degree_files || [],
    predefined_certificate_files: requestValues.predefined_certificate_files || [],
    additional_certificate_files: requestValues.additional_certificate_files || [],
    employer_confirmation_files: requestValues.employer_confirmation_files || [],
    finsa_certificate_files: finsaResetActive ? [] : requestValues.finsa_certificate_files || [],
    proof_of_code_of_conduct_training_files: requestValues.proof_of_code_of_conduct_training_files || [],

    additional_recipients: isRenewal ? getSessionStorageEmail : requestValues.additional_recipients || '',
  };

  const { PAYMENTS_CONFIRMATIONS, PROOF_OF_CODE_OF_CONDUCT } = STEP_VARIANT;

  const steps = isEmployerChange
    ? [
        [STEP_VARIANT.REQUEST_COMPANY_CHANGE, t('request.summary.headings.requestEmployerChange')],
        [STEP_VARIANT.COMPANY_INFORMATION, t('request.summary.headings.businessInformation')],
        [STEP_VARIANT.SUMMARY, t('request.summary.headings.summary')],
      ]
    : [
        [STEP_VARIANT.PERSONAL_INFORMATION, t('request.summary.headings.personalInformation')],
        [STEP_VARIANT.FIELDS_OF_ACTIVITY, t('request.summary.headings.fieldsOfActivity')],
        [STEP_VARIANT.PROFFESSIONAL_ACADEMIC_DEGREES, t('request.summary.headings.professionalKnowledge')],
        [STEP_VARIANT.PROOF_OF_CODE_OF_CONDUCT, t('request.summary.headings.proofOfCodeOfConduct')],
        [STEP_VARIANT.COMPANY_INFORMATION, t('request.summary.headings.businessInformation')],
        [STEP_VARIANT.SUMMARY, t('request.summary.headings.summary')],
      ];

  const isInitial = !isAmendment && !isUpdateRequire;
  (isInitial || isRenewal || isEmployerChange) &&
    steps.push([STEP_VARIANT.PAYMENTS_CONFIRMATIONS, t('request.summary.headings.conclusion')]);

  const stepKeys = steps.map((step) => step[0]);

  const { REQUEST_FORM } = FORM_VARIANT;
  const isFinalStep = activeStep === steps.length;
  const [editForm, setEditForm] = useState(!isRenewal);

  /* Previous version had different values, we need to check if finsa_certificate_files exists */
  const isCodeOfConduct = stepKeys[activeStep - 1] === PROOF_OF_CODE_OF_CONDUCT;

  const [employerChange, submitEmployerChange] = useValidation(employerChangeValidationSchema(user.is_admin));
  const [personal, submitPersonal] = useValidation(personalValidationSchema(user.is_admin));
  const [fields, submitFields] = useValidation(activityValidationSchema(user.is_admin));
  const [professional, submitProfessional] = useValidation(professionalValidationSchema(user.is_admin));
  const [codeOfConduct, submitcodeOfConduct] = useValidation(codeOfConductValidationSchema(user.is_admin));
  const [company, submitCompany] = useValidation(isEmployerChange ? employerChangeCompanyValidationSchema(user.is_admin) : companyValidationSchema(user.is_admin));
  const [summary, submitSummary] = useValidation(
    isAmendment || !isInitial ? formValidationSchema(user.is_admin) : Yup.object().shape({})
  );
  const [confirmations, submitConfirmations] = useValidation(
    isRenewal ? detailsRenewalValidationSchema() : isEmployerChange ? detailsEmployerChangeValidationSchema() : detailsInitialValidationSchema()
  );

  const validations = isEmployerChange
    ? [employerChange, company, summary, confirmations]
    : [personal, fields, professional, codeOfConduct, company, summary, confirmations];

  const submitValidations = isEmployerChange
    ? [submitEmployerChange, submitCompany, submitSummary, submitConfirmations]
    : [
        submitPersonal,
        submitFields,
        submitProfessional,
        submitcodeOfConduct,
        submitCompany,
        submitSummary,
        submitConfirmations,
      ];

  const currentValidationSchema = validations[activeStep - 1];

  return (
    <AdvisorHeader>
      <FormWrapper isAdvisor={true}>
        <RequestStepWrapper
          adminRedirect="/request-details"
          finalRedirect={`/request-form/confirmation/${request.case_id}`}
          goBack={user.is_admin ? '/dashboard' : null}
          validationSubmit={submitValidations[activeStep - 1]}
          formVariant={REQUEST_FORM}
          formRef={formRef}
          steps={steps}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          isIntro={intro}
          isRenewal={isRenewal}
          isFinal={isFinalStep}
          setEditForm={setEditForm}
          editForm={editForm}
          isCodeOfConduct={isCodeOfConduct}
          setIsSummaryCompleted={setIsSummaryCompleted}
          isEmployerChange={isEmployerChange}
          isAdvisor
          isUpdateRequire={isUpdateRequire}
        >
          <>
            {isAmendment && intro && <Intro isAmendment={isAmendment} isRenewal={isRenewal} isEmployerChange={isEmployerChange} showIntro={showIntro} />}
            {stepKeys[activeStep - 1] !== PAYMENTS_CONFIRMATIONS && !intro && (
              <Formik
                // oxlint-disable-next-line react-hooks/refs
                enableReinitialize={canReinitialise.current}
                innerRef={formRef}
                initialValues={initialValues}
                onSubmit={onFormSubmit}
                validationSchema={currentValidationSchema}
              >
                {({ values, errors, touched }) => (
                  <div className="request-form__step">
                    <Step
                      activeStep={steps[activeStep - 1]}
                      values={values}
                      errors={errors}
                      touched={touched}
                      user={user}
                      editForm={editForm}
                      isRenewal={isRenewal}
                      isEmployerChange={isEmployerChange}
                      isAmendment={isAmendment}
                    />
                  </div>
                )}
              </Formik>
            )}
            {stepKeys[activeStep - 1] === PAYMENTS_CONFIRMATIONS && !intro && (
              <Confirmations validationSchema={currentValidationSchema} isRenewal={isRenewal} isEmployerChange={isEmployerChange} />
            )}
          </>
        </RequestStepWrapper>
      </FormWrapper>
    </AdvisorHeader>
  );
};

export default RequestForm;
